import type { RaceSettlement } from "./race-settlement.js";

/**
 * 開獎之後，一注算對還是算錯、誰站上頒獎台。兩季共用。
 *
 * 只回答「結果是什麼」，稱號與措辭由畫面決定，與 `battleReport()` 同一個分法。與
 * `src/domain/` 的鐵則一致：純函式，不讀系統時間，名字由呼叫端傳入。
 */

export type BetStatus = "pending" | "void" | "correct" | "wrong";
export type BetTone = "hit" | "mixed" | "off";

export interface RankedRow {
  nickname: string;
  diff: number | null;
  rank: number | null;
}

export interface BetResult {
  status: BetStatus;
  rank: number | null;
  diff: number | null;
}

/**
 * 排名放上領獎台。一階可能站好幾個人，而 1、1、3 這種跳號時第二階就是空的：
 * 階數照名次走，不把第三名往上挪。第四名以後在台下。
 */
export function podiumSteps<T extends RankedRow>(ranking: T[]): {
  first: T[];
  second: T[];
  third: T[];
  others: T[];
} {
  return {
    first: ranking.filter(row => row.rank === 1),
    second: ranking.filter(row => row.rank === 2),
    third: ranking.filter(row => row.rank === 3),
    others: ranking.filter(row => (row.rank ?? Infinity) > 3)
  };
}

/** 結算之後，不在排名裡的人就是猜錯了。 */
export function betResult(ranking: RankedRow[], settled: boolean, nickname: string): BetResult {
  if (!settled) return { status: "pending", rank: null, diff: null };
  const row = ranking.find(entry => entry.nickname === nickname);
  return row
    ? { status: "correct", rank: row.rank, diff: row.diff }
    : { status: "wrong", rank: null, diff: null };
}

/** 第二季的一個人兩題各自的結果。取消的完賽題不算對也不算錯。 */
export function raceBetResults(settlement: RaceSettlement, nickname: string): {
  weight: BetResult;
  finish: BetResult;
} {
  const { weight, finish } = settlement;
  return {
    weight: betResult(weight.ranking, weight.settled, nickname),
    finish: finish.outcome === "cancelled"
      ? { status: "void", rank: null, diff: null }
      : betResult(finish.ranking, finish.outcome !== "pending", nickname)
  };
}

/** 兩題都猜對的人，依體重題排名的順序。任一題還沒結算或取消就沒有人。 */
export function doublePredictors(settlement: RaceSettlement): string[] {
  const finishers = new Set(settlement.finish.ranking.map(row => row.nickname));
  return settlement.weight.ranking
    .map(row => row.nickname)
    .filter(nickname => finishers.has(nickname));
}

/**
 * 名單那一列的底色。全對、對錯參半、全錯各一種；還沒有任何一題開獎是 null，
 * 這時候畫面維持押哪邊的配色。
 */
export function betTone(statuses: BetStatus[]): BetTone | null {
  const hits = statuses.filter(status => status === "correct").length;
  const misses = statuses.filter(status => status === "wrong").length;
  if (hits === 0 && misses === 0) return null;
  if (misses === 0) return "hit";
  return hits === 0 ? "off" : "mixed";
}
